import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, AlertTriangle, Database, ChevronDown, Plus, Search, Lock, X } from "lucide-react";
import { autoCategoriesOf } from "../lib/collections";
import { useClickOutside, useNeedCategories } from "../lib/hooks";

const VARIANTS = {
  green: {
    trigger: "hover:border-green-300 focus-within:border-green-500",
    plus: "text-green-500",
    filterFocus: "focus:border-green-500",
    option: "hover:bg-green-50 hover:border-green-100",
    chip: "bg-green-50 text-green-700 border-green-100",
    badge: "bg-green-50 text-green-700",
  },
  amber: {
    trigger: "hover:border-amber-300 focus-within:border-amber-500",
    plus: "text-amber-500",
    filterFocus: "focus:border-amber-500",
    option: "hover:bg-amber-50 hover:border-amber-100",
    chip: "bg-amber-50 text-amber-700 border-amber-100",
    badge: "bg-amber-50 text-amber-700",
  },
};

function AddCategoryDropdown({ options, onSelect, disabled, v }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, () => setOpen(false));

  const filtered = options.filter(o => o.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        className={`flex items-center justify-between w-full h-[52px] px-5 border-2 rounded-2xl bg-white transition-all
          ${disabled ? "opacity-50 cursor-not-allowed border-slate-100" : `border-slate-100 ${v.trigger} shadow-sm cursor-pointer`}`}
      >
        <span className="flex items-center gap-2 text-slate-500 font-bold text-sm">
          <Plus size={16} className={v.plus} /> Add category
        </span>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && !disabled && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute z-[100] w-full mt-1 bg-white border border-slate-200 rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.12)] overflow-hidden"
        >
          <div className="px-4 pt-3 flex items-center gap-2">
            <Search size={16} className="text-slate-400" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter..."
              className={`w-full px-2 py-2 text-sm font-semibold rounded-xl bg-slate-50 border border-slate-100 outline-none focus:bg-white ${v.filterFocus}`}
              autoFocus
            />
          </div>
          <div className="p-3 grid grid-cols-1 gap-1.5 max-h-64 overflow-y-auto custom-scrollbar">
            {filtered.length === 0 && (
              <div className="text-xs font-semibold text-slate-400 px-4 py-3 italic">
                Nothing left to add
              </div>
            )}
            {filtered.map((cat) => (
              <div
                key={cat}
                onClick={() => { onSelect(cat); setOpen(false); setFilter(""); }}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all cursor-pointer text-slate-600 border border-transparent ${v.option}`}
              >
                <span className="text-sm font-semibold select-none truncate">{cat}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}

export function CollectionCategoryPicker({
  collections = [],
  value = {},
  onChange,
  errors = {},
  loading = false,
  variant = "green",
}) {
  const v = VARIANTS[variant] || VARIANTS.green;
  const { categories, error: catLoadError } = useNeedCategories();

  const addCategory = (collectionId, autoCats, category) => {
    const current = Array.isArray(value[collectionId]) ? value[collectionId] : [...autoCats];
    if (current.includes(category)) return;
    onChange({ ...value, [collectionId]: [...current, category] });
  };

  const removeCategory = (collectionId, autoCats, category) => {
    // Auto-detected categories are non-removable.
    if (autoCats.includes(category)) return;
    const current = Array.isArray(value[collectionId]) ? value[collectionId] : [...autoCats];
    onChange({ ...value, [collectionId]: current.filter(c => c !== category) });
  };

  if (loading) {
    return (
      <div className="text-sm font-semibold text-slate-400 italic">
        Discovering collections...
      </div>
    );
  }

  if (!collections.length) {
    return (
      <div className="text-sm font-semibold text-slate-400 italic">
        No collections discovered yet. Enter a valid endpoint URL above.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {catLoadError && (
        <div className="text-xs font-bold text-red-600">{catLoadError}</div>
      )}
      <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">
        {collections.length} collection{collections.length === 1 ? "" : "s"} discovered
      </div>
      <div className="space-y-3">
        {collections.map((c) => {
          const autoCats = autoCategoriesOf(c);
          // Selected = parent value if present, otherwise the locked auto set.
          const selected = Array.isArray(value[c.id]) ? value[c.id] : [...autoCats];
          // Keep auto cats always present even if a stale value dropped them.
          const merged = [...new Set([...autoCats, ...selected])];
          const missing = autoCats.length === 0 && merged.length === 0;
          const rowError = errors[c.id] || (missing ? "Pick at least one category" : null);
          const addable = categories.filter(cat => !merged.includes(cat));

          return (
            <motion.div
              key={c.id}
              layout
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-2xl border-2 p-5 bg-white transition-all
                ${rowError ? "border-red-300 ring-4 ring-red-50" : "border-slate-100"}
              `}
            >
              <div className="flex flex-col md:flex-row md:items-start md:gap-6">
                <div className="flex-1 min-w-0 mb-4 md:mb-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Database size={14} className="text-slate-400 flex-shrink-0" />
                    <code className="font-mono text-xs font-bold text-slate-700 truncate">
                      {c.id}
                    </code>
                    {autoCats.length > 0 && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${v.badge} rounded-full text-[9px] font-black uppercase tracking-widest`}>
                        <CheckCircle2 size={10} /> Auto
                      </span>
                    )}
                    {missing && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-[9px] font-black uppercase tracking-widest">
                        <AlertTriangle size={10} /> Pick category
                      </span>
                    )}
                  </div>
                  {c.title && (
                    <div className="text-sm font-bold text-slate-800 truncate">
                      {c.title}
                    </div>
                  )}
                  {c.description && (
                    <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {c.description}
                    </div>
                  )}
                </div>
                <div className="md:w-80 flex-shrink-0 space-y-3">
                  {merged.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {merged.map((cat) => {
                        const locked = autoCats.includes(cat);
                        return (
                          <span
                            key={cat}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border
                              ${locked
                                ? v.chip
                                : "bg-slate-100 text-slate-700 border-slate-200"}`}
                          >
                            {locked && <Lock size={11} className="opacity-70" />}
                            <span className="truncate max-w-[180px]">{cat}</span>
                            {!locked && (
                              <button
                                type="button"
                                onClick={() => removeCategory(c.id, autoCats, cat)}
                                className="hover:text-red-500 transition-colors"
                                aria-label={`Remove ${cat}`}
                              >
                                <X size={13} />
                              </button>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <AddCategoryDropdown
                    options={addable}
                    onSelect={(cat) => addCategory(c.id, autoCats, cat)}
                    v={v}
                  />
                  {rowError && (
                    <div className="text-[10px] font-bold text-red-600 uppercase tracking-widest">
                      {rowError}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
